import React, { useEffect, useRef, useState } from 'react';
import { parseCartCsv } from '../utils/cartCsv';
import './CartCsvImportReview.css';

// Keep the source rows separate from the live cart until the whole import succeeds.
export function applyCsvChoices(rows, choices) {
  return rows.filter(row => !choices[row.rowNumber]?.skip).map(row => {
    const card = choices[row.rowNumber];
    return card ? { ...row, productId: card.productId, name: card.name, set: card.set || '', number: card.number || '', skuId: String(card.productId) === String(row.productId) ? row.skuId : '' } : row;
  });
}

export function useCartCsvImport(importCartItems) {
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const runImport = async (rows, fileName) => {
    setBusy(true);
    try {
      if (!importCartItems) throw new Error('CSV import is unavailable. Please refresh the buylist.');
      const count = await importCartItems(rows);
      setReview(null);
      alert(`Successfully imported ${count} items from CSV. Prices have been updated to the current buylist rates.`);
    } catch (error) {
      setReview({ rows, fileName, message: error.message || 'The import could not be completed.', issues: error.issues || [] });
    } finally { setBusy(false); }
  };
  const onFileChange = async event => {
    const input = event.target;
    const file = input.files[0];
    if (!file) return;
    setBusy(true);
    try {
      const rows = parseCartCsv(await file.text());
      await runImport(rows, file.name);
    } catch (error) {
      setReview({ rows: [], fileName: file.name, message: error.message || 'The CSV file could not be read.', issues: error.issues || [] });
    } finally { input.value = ''; setBusy(false); }
  };
  return { onFileChange, busy, reviewProps: { review, busy, onClose: () => !busy && setReview(null), onRetry: choices => runImport(applyCsvChoices(review.rows, choices), review.fileName) } };
}

export default function CartCsvImportReview({ review, busy, onClose, onRetry, dark = false }) {
  const [choices, setChoices] = useState({});
  const dialogRef = useRef(null);
  useEffect(() => {
    setChoices({});
    if (!review) return undefined;
    const previousFocus = document.activeElement;
    dialogRef.current?.focus();
    return () => previousFocus?.focus?.();
  }, [review]);
  if (!review) return null;
  const issues = review.issues;
  const skippedRows = review.rows.filter(row => choices[row.rowNumber]?.skip);
  const skippedCopies = skippedRows.reduce((count, row) => count + row.quantity, 0);
  const onKeyDown = event => {
    if (event.key === 'Escape' && !busy) onClose();
    if (event.key !== 'Tab') return;
    const controls = [...dialogRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled)')];
    const first = controls[0], last = controls[controls.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  return <div className={`csv-review-backdrop${dark ? ' csv-review-dark' : ''}`}>
    <section className="csv-review-dialog" role="dialog" aria-modal="true" aria-labelledby="csv-review-title" ref={dialogRef} tabIndex={-1} onKeyDown={onKeyDown}>
      <header className="csv-review-header"><div><span className="csv-review-eyebrow">CSV IMPORT REVIEW</span><h2 id="csv-review-title">{issues.length ? 'Some rows need your attention' : 'We could not import this file'}</h2><p>{review.fileName}</p></div><button type="button" aria-label="Close import review" onClick={onClose} disabled={busy}>×</button></header>
      <div className="csv-review-summary" role="status"><strong>Your existing cart is safe.</strong> No cards from this file have been added. {issues.length > 0 && <span>{issues.length} row{issues.length === 1 ? '' : 's'} to review{review.rows.length > 0 ? ` out of ${review.rows.length}` : ''}.</span>}</div>
      <div className="csv-review-content">
        {!issues.length && <p>{review.message}</p>}
        {issues.map((issue, index) => <article className="csv-review-row" key={`${issue.rowNumber}-${index}`}>
          <div className="csv-review-row-heading"><span className="csv-review-badge">Row {issue.rowNumber}</span><h3>{issue.name || 'Card details'}</h3></div><p>{issue.message}</p>
          <fieldset><legend>{issue.candidates?.length ? 'Choose the intended card or leave this row out' : 'Correct this row in your CSV, or choose to leave it out'}</legend>{(issue.candidates || []).map((card, candidateIndex) => <label className="csv-review-choice" key={`${card.productId}-${candidateIndex}`}><input type="radio" name={`csv-row-${issue.rowNumber}`} checked={choices[issue.rowNumber] === card} disabled={busy} onChange={() => setChoices(current => ({ ...current, [issue.rowNumber]: card }))} /><span><strong>{card.name}</strong><small>{[card.set, card.number ? `#${card.number}` : '', `Product ID ${card.productId}`].filter(Boolean).join(' · ')}</small></span></label>)}<label className="csv-review-choice"><input type="radio" name={`csv-row-${issue.rowNumber}`} checked={choices[issue.rowNumber]?.skip === true} disabled={busy} onChange={() => setChoices(current => ({ ...current, [issue.rowNumber]: { skip: true } }))} /><span><strong>Leave this row out of the import</strong><small>{review.rows.find(row => row.rowNumber === issue.rowNumber)?.quantity || 0} copies will be left out. All other rows will still be checked.</small></span></label></fieldset>
        </article>)}
      </div>
      <footer className="csv-review-footer"><p>Quantities, condition, printing and language stay as supplied in your CSV.{skippedRows.length > 0 && <strong> You chose to leave out {skippedRows.length} row{skippedRows.length === 1 ? '' : 's'} ({skippedCopies} copies).</strong>}</p><div><button type="button" onClick={onClose} disabled={busy}>Close</button>{review.rows.length > 0 && <button type="button" className="csv-review-primary" onClick={() => onRetry(choices)} disabled={busy || skippedRows.length === review.rows.length || (issues.length > 0 && Object.keys(choices).length === 0)}>{busy ? 'Checking all rows…' : 'Retry import'}</button>}</div></footer>
    </section>
  </div>;
}
